export const mockData = {
  // Hero Section
  hero: {
    headline: "Your time back.\nYour life amplified.",
    subheading: "The repetitive admin — chasing leads, retyping invoices, rebuilding the same reports, digging out of your inbox — shouldn't cost you your evenings. We build the AI automations that handle it, done-for-you, so you can run your business instead of its paperwork.",
    ctaButtons: [
      { text: "Tell us what's eating your time", type: "primary", action: "contact" },
      { text: "See what we automate", type: "secondary", action: "services" }
    ]
  },

  // What we automate (the offer — right under the hero)
  services: {
    title: "What we can automate for you",
    subtitle: "Fixed-scope, done-for-you automations that pay for themselves in saved hours — we build them and run them, so there's nothing for you to babysit.",
    founderNote: "Built and run by Matthew — every automation we sell, we use ourselves first.",
    // Bounded on Matthew's call, 2026-09-16: the old wording ("we keep working free until
    // it does — or you don't pay") never said who elects which, so it read as unlimited
    // free labour. Same risk reversal for the buyer; exposure capped at the project fee.
    guarantee: "Our guarantee: we scope the hours your automation will save. If it doesn't hit that number within 30 days of handoff, we keep working on that workflow until it does, or we refund what you paid.",
    items: [
      {
        name: "Lead-Intake Autopilot",
        outcome: "Never lose a lead to a slow reply",
        accent: "blue",
        description: "Turn web forms and inquiries into qualified leads in your CRM, with an instant AI-drafted first reply so no one waits.",
        icon: "target"
      },
      {
        name: "Paperwork Killer",
        outcome: "Stop retyping the same data",
        accent: "pink",
        description: "Automate one document workflow end to end — invoices, reports, data moving between systems — with a human review queue.",
        icon: "file"
      },
      {
        name: "Inbox Triage",
        outcome: "Reclaim an hour a day",
        accent: "amber",
        description: "AI sorts and routes your email and drafts replies for your most common requests, so your inbox runs itself.",
        icon: "mail"
      },
      {
        name: "Your custom workflow",
        outcome: "If it's repetitive, it's automatable",
        accent: "green",
        description: "Have a time-drain that doesn't fit a box? Tell us what's eating your time and we'll scope an automation for it.",
        icon: "blueprint"
      }
    ]
  },

  // Sample Blueprint (proof — what you walk away with)
  proof: {
    title: "In two minutes, you'll have a first draft",
    subtitle: "Chat with our AI concierge and you walk away with a rough Automation Blueprint for your exact workflow. Matthew reads it himself and sends you the worked-up version within two business days. Here's a sample of the finished one.",
    label: "Sample Automation Blueprint",
    sections: [
      { h: "Your workflow today", b: "You spend about 4 hours a week retyping invoices from email into QuickBooks, roughly 30 a week, one at a time." },
      { h: "The automated version", b: "Incoming invoice emails are read automatically, the key details extracted, and each one entered into QuickBooks, with a review queue for anything unusual." },
      { h: "What we'd connect", b: "Your email inbox, QuickBooks" },
      { h: "Time you'd get back", b: "About 4 hours every week." },
      { h: "Recommended package", b: "Paperwork Killer ($1,200 to $2,500), plus an optional care plan from $95/mo." }
    ],
    cta: "Get your draft — start the chat"
  },

  // How It Works Section
  howItWorks: {
    title: "How we get your time back",
    subtitle: "Four steps to hand off the work that drains you.",
    steps: [
      {
        number: "01",
        title: "Discover",
        accent: "blue",
        description: "We identify the repetitive tasks that drain your time and energy.",
        icon: "search"
      },
      {
        number: "02",
        title: "Design",
        accent: "pink",
        description: "A fixed-scope automation tailored to your specific tools and workflow.",
        icon: "blueprint"
      },
      {
        number: "03",
        title: "Deploy",
        accent: "amber",
        description: "We build, test, and hand it off — with the documentation to run it.",
        icon: "rocket"
      },
      {
        number: "04",
        title: "Thrive",
        accent: "green",
        description: "You get hours back every week while the busywork runs itself.",
        icon: "sparkles"
      }
    ]
  },

  // Announcement pill in the hero (points at #products)
  announcement: {
    text: "New from Automation Vita: MinutesBack, leave-now texts for MARC riders, and SmartValueDirect, a personal market analyst.",
    linkText: "See both",
    href: "#products"
  },

  // Our products (built for ourselves first, now sold on their own).
  // Offer terms mirror each product's own site — MinutesBack's come from
  // `disclosure.py` in the MinutesBack repo, which is its single source of
  // truth for what a rider is promised before they consent. If a price or a
  // trial length changes there, change it here in the same session.
  products: {
    title: "Two products we built for ourselves",
    subtitle: "Every automation we sell, we use first. These two started as tools for our own commute and our own portfolio.",
    founderNote: "Both are built and run by Matthew on the same AWS stack we use for client work.",
    products: [
      {
        name: "MinutesBack",
        // wordmark split so the second half can carry the product's accent
        wordmark: ["Minutes", "Back"],
        outcome: "Stop waiting on the platform for a late train.",
        description: "A text that tells MARC riders when to leave home, timed to live train data and your walk to the station. If your train is 12 minutes late, you find out at your kitchen table. No app. One text to set up.",
        price: "99 cents for your first 21 days, then $39 a year or $4.99 a month.",
        note: "Card required. Text STOP any time. Not affiliated with MDOT MTA.",
        status: "Live on MARC",
        link: "https://minutesback.com/?c=AVSITE",
        // Matthew confirmed the number round-trips a real signup text, 2026-09-16.
        ctaText: "Start for 99 cents",
        mark: "minutesback",
        accent: "#F26B1D",
        action: "#14213D",
        actionHover: "#0C1528"
      },
      {
        name: "SmartValueDirect",
        wordmark: ["SmartValue", "Direct"],
        outcome: "Know what today means for your portfolio.",
        description: "A personal market analyst that reads your holdings every morning, answers your questions before the open, and grades every one of its own calls in public, losses included.",
        price: "Leave your email and you'll hear the morning it opens.",
        note: "Analysis and education, not investment advice.",
        status: "Opening soon",
        link: "https://smartvaluedirect.com/?utm_source=automationvita&utm_medium=web&utm_campaign=launch",
        ctaText: "Join the waitlist",
        mark: "smartvaluedirect",
        accent: "#c41e5a",
        action: "#c41e5a",
        actionHover: "#9d1848"
      }
    ]
  },

  // Business intake (primary contact)
  intake: {
    title: "Tell us what's eating your time",
    subtitle: "Describe the repetitive task draining your week. Our concierge drafts you a blueprint on the spot, and Matthew sends the worked-up version within two business days.",
    namePlaceholder: "Your name",
    emailPlaceholder: "Your email",
    messagePlaceholder: "What repetitive task is eating your time? (e.g. \"I retype every invoice into QuickBooks\")",
    buttonText: "Send it over",
    successTitle: "Got it — thank you.",
    successBody: "We read every one of these. Expect a reply from Matthew within two business days."
  },

  // Newsletter (secondary)
  newsletter: {
    title: "Or just follow along",
    description: "Automation tips and what we're building — occasionally, in your inbox.",
    placeholder: "Enter your email address",
    buttonText: "Subscribe",
    success: "Thanks — you're on the list."
  },

  // Contact & Social
  contact: {
    email: "info@automationvita.com",
    website: "https://www.automationvita.com/",
    // YouTube pulled 2026-09-16: the channel has no videos and is titled "The Automation
    // Life", so the link cost trust rather than building it. Restore it once there is
    // content and the channel name matches the brand.
    social: [
      {
        platform: "X (Twitter)",
        handle: "@automationvita",
        url: "https://twitter.com/automationvita",
        icon: "twitter"
      }
    ]
  },

  // Footer product links
  footerLinks: [
    { label: "MinutesBack", href: "https://minutesback.com/?c=AVSITE" },
    { label: "SmartValueDirect", href: "https://smartvaluedirect.com/?utm_source=automationvita&utm_medium=web&utm_campaign=launch" },
    { label: "Privacy Policy", href: "/privacy.html" }
  ],

  // Navigation
  navigation: [
    { label: "What we automate", href: "#services" },
    { label: "How it works", href: "#how-it-works" },
    { label: "Products", href: "#products" },
    { label: "Contact", href: "#contact", isButton: true }
  ],

  // Founder story — reserved for the SmartValueDirect site, not shown on the Automation Vita homepage.
  founder: {
    eyebrow: "Why I built SmartValueDirect",
    title: "Built for myself first.",
    paragraphs: [
      "In 2016 I rolled an old 401(k) into an IRA and decided to actually learn how to invest — not chase tips, but build something I understood. Years of books, podcasts, and Valueline reports later, I had a strategy that fit my risk tolerance and, just as importantly, my temperament. Discipline was the whole point.",
      "A couple of years ago I started using AI to pressure-test my thinking and keep me honest. It became a report I read every morning before I make a single decision — and it changed the way I invest.",
      "SmartValueDirect is me sharing that with you. Not hype, not hot tips — a way to help you make calmer, more disciplined decisions, and over time, a report shaped around your experience and your risk tolerance, not just mine."
    ],
    author: "Matthew",
    authorTitle: "Founder, Automation Vita",
    authorNote: "Investing this way since 2016"
  }
};
